import * as React from 'react'
import _ from 'lodash';
import { isFunction } from '@Utils/Micro';
import InputGroup from "rsuite/lib/InputGroup";
import Input from "rsuite/lib/Input";
import InputNumber from "rsuite/lib/InputNumber";
// ================================================================================================
const WAIT_INTERVAL = 250;
const ENTER_KEY = 13;
// ================================================================================================
export interface TextBoxProps<T> {
    type: "number" | "text" | "textarea" | "email" | "password" | "ip";

    onChangeString: (value: T) => void;

    /** An input can show that it is disabled */
    disabled?: boolean;

    /** An input can show that it is disabled */
    readonly?: boolean;

    /** Minimum value */
    min?: number;

    /** Maximum value */
    max?: number;

    /** The value of each step. can be decimal */
    step?: number;

    /** Current value of the input. Creates a controlled component */
    value?: T;

    /** Sets the element displayed on the right side of the component */
    postfix?: string;
    postfixIcon?: string;
    postfixIsButton?: boolean;

    /** Sets the element displayed to the left of the component */
    prefix?: string;
    prefixIcon?: string;
    prefixIsButton?: boolean;

    /** Create full width support */
    fullWidth?: boolean;

    /** override styles */
    style?: React.CSSProperties;

    /** override styles */
    containerStyle?: React.CSSProperties;

    /** Error handling */
    errorText?: string;
    errorStyle?: React.CSSProperties;

    /** number of rows in textarea */
    rows?: number;

    onClick?: (isPrefix: boolean) => void;
    onEnterPress?: () => void;

    autoFocus?: boolean;
    placeholder?: string;
}
// ------------------------------------------------------------------------------------------------
export interface TextBoxState<T> {
    value: T;
    origValue: T;
}
// ------------------------------------------------------------------------------------------------
export default class TextBox<T> extends React.Component<TextBoxProps<T>, TextBoxState<T>> {
    private timer: number;
    // --------------------------------------------------------------------------------------------
    constructor(props: Readonly<TextBoxProps<T>>) {
        super(props);
        this.state = {
            value: this.props.value,
            origValue: this.props.value
        };
    }
    // --------------------------------------------------------------------------------------------
    static getDerivedStateFromProps(nextProps: TextBoxProps<any>, prevState: TextBoxState<any>) {
        if (!_.isEqual(nextProps.value, prevState.origValue))
            return { value: nextProps.value, origValue: nextProps.value };
        return null;
    }
    // --------------------------------------------------------------------------------------------
    handleChange = (value, event) => {
        window.clearTimeout(this.timer);
        this.setState({ value: value });
        this.timer = window.setTimeout(this.triggerChange, WAIT_INTERVAL);
    }
    // --------------------------------------------------------------------------------------------
    handleKeyDown = e => {
        if (e.keyCode === ENTER_KEY) {
            window.clearTimeout(this.timer);
            this.triggerChange();

            if (isFunction(this.props.onEnterPress))
                this.props.onEnterPress();
        }
    }
    // --------------------------------------------------------------------------------------------
    onBlur = e => {
        window.clearTimeout(this.timer)
        this.triggerChange();
    }
    // --------------------------------------------------------------------------------------------
    triggerChange = () => {
        if (this.props.type == "number") {
            let value = parseFloat(this.state.value as unknown as string);
            if (!_.isNumber(value) ||
                _.isNaN(value))
                return;
        }

        if (isFunction(this.props.onChangeString))
            this.props.onChangeString(this.state.value);
    }
    // --------------------------------------------------------------------------------------------
    render() {
        const { value, fullWidth,
            postfix, postfixIcon,
            prefix, prefixIcon, style,
            errorStyle, errorText,
            prefixIsButton, postfixIsButton,
            onClick, readonly, disabled,
            max, min, onChangeString,
            autoFocus, containerStyle,
            placeholder, ...rest } = this.props
        var inStyle: React.CSSProperties = { ...style };
        if (fullWidth)
            inStyle.width = "100%";

        // For readonly we show just the number 
        if (this.props.type == "number" && !readonly) {
            let postfixFinal: React.ReactNode = postfix;
            if (postfixIcon != null)
                postfixFinal = <i className={postfixIcon}
                    onClick={(postfixIsButton) ? () => onClick?.(false) : undefined}
                    style={{ cursor: (postfixIsButton) ? "pointer" : undefined }} />;
            let prefixFinal: React.ReactNode = prefix;
            if (prefixIcon != null)
                prefixFinal = <i className={prefixIcon}
                    onClick={(postfixIsButton) ? () => onClick?.(true) : undefined}
                    style={{ cursor: (postfixIsButton) ? "pointer" : undefined }} />;
            return <div style={{ width: "100%", ...containerStyle }}>
                <InputGroup>
                    <InputNumber
                        value={this.state.value as unknown as number}
                        onChange={(readonly) ? _.noop : this.handleChange}
                        onKeyDown={(readonly) ? _.noop : this.handleKeyDown}
                        onBlur={(readonly) ? _.noop : this.onBlur}
                        style={inStyle}
                        postfix={postfixFinal}
                        readOnly={readonly}
                        disabled={disabled}
                        prefix={prefixFinal}
                        max={(max == null) ? undefined : max}
                        min={(min == null) ? undefined : min}
                        autoFocus={autoFocus}
                        {...rest}
                    />
                </InputGroup>
                {(errorText != null) ? <div className="settingslist-error" style={errorStyle}>{errorText}</div> : null}
            </div>
        }

        return <div style={{ width: "100%", ...containerStyle }}>
            <InputGroup>
                {(prefix != null) ? (prefixIsButton ? <InputGroup.Button onClick={() => onClick?.(true)}>{prefix}</InputGroup.Button> : <InputGroup.Addon>{prefix}</InputGroup.Addon>) : null}
                {(prefixIcon != null) ? (prefixIsButton ? <InputGroup.Button onClick={() => onClick?.(true)}><i className={prefixIcon} /></InputGroup.Button> : <InputGroup.Addon><i className={prefixIcon} /></InputGroup.Addon>) : null}
                <Input
                    type={this.props.type}
                    componentClass={(this.props.type == "textarea") ? "textarea" : undefined}
                    value={this.state.value as unknown as string}
                    onChange={(readonly) ? _.noop : this.handleChange}
                    onKeyDown={(readonly) ? _.noop : this.handleKeyDown}
                    onBlur={(readonly) ? _.noop : this.onBlur}
                    style={inStyle}
                    readOnly={readonly}
                    disabled={disabled}
                    autoFocus={autoFocus}
                    placeholder={placeholder}
                    {...rest}
                />
                {(postfix != null) ? (postfixIsButton ? <InputGroup.Button onClick={() => onClick?.(false)}>{postfix}</InputGroup.Button> : <InputGroup.Addon>{postfix}</InputGroup.Addon>) : null}
                {(postfixIcon != null) ? (postfixIsButton ? <InputGroup.Button onClick={() => onClick?.(false)}><i className={postfixIcon} /></InputGroup.Button> : <InputGroup.Addon><i className={postfixIcon} /></InputGroup.Addon>) : null}
            </InputGroup>
            {(errorText != null) ? <div className="settingslist-error" style={errorStyle}>{errorText}</div> : null}
        </div>
    }
    // --------------------------------------------------------------------------------------------
}